import { useEffect, useRef } from 'react';
import './types';

/** Map our app theme to Google's button theme. Google offers three presets
 *  (`outline`, `filled_blue`, `filled_black`) and no custom-color option. */
function googleButtonTheme(appTheme: 'light' | 'dark'): 'outline' | 'filled_black' {
  return appTheme === 'dark' ? 'filled_black' : 'outline';
}

/** Google's `renderButton` clamps `width` to [200, 400]. */
function clampGoogleWidth(px: number): number {
  return Math.max(200, Math.min(400, Math.round(px)));
}

/** Renders Google's official sign-in button via Google Identity Services.
 *  The button is rendered inside a Google-controlled iframe — we cannot
 *  style it beyond the preset options Google exposes (theme/size/shape/
 *  text/width). Brand-color matching requires the OAuth code flow + a
 *  backend exchange endpoint instead; out of scope here. */
export function useGoogleSignIn(
  clientId: string,
  onCredential: (response: { credential: string }) => void,
  appTheme: 'light' | 'dark' = 'light',
) {
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;

    const render = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return;
      window.google.accounts.id.initialize({ client_id: clientId, callback: onCredential });
      const width = clampGoogleWidth(buttonRef.current.offsetWidth || 360);
      buttonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: googleButtonTheme(appTheme),
        size: 'large',
        width,
        text: 'signin_with',
        shape: 'rectangular',
      });
    };

    // GIS script in index.html is `async defer` with no load callback, so
    // poll until `window.google.accounts.id` exists.
    if (window.google?.accounts?.id) {
      render();
      return;
    }
    const t = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(t);
        render();
      }
    }, 100);
    return () => clearInterval(t);
  }, [clientId, onCredential, appTheme]);

  return buttonRef;
}
